import React from 'react'
import {
  fulfillActivatedTrade,
  fulfillCancelTrade,
  fulfillCloseTrade,
  fulfillCurrentPrices,
  fulfillModifyPendingTrade,
  fulfillModifyTrade,
  fulfillOpenTrade,
  fulfillPendingTrade,
  fulfillTick,
} from '../actions/TradeActions'
import {
  fulfillAssetGroups,
  fulfillPrices,
  fulfillLogin,
  fulfillCurrentMarketState,
} from '../actions/mt4Actions'
import { initApp, triggerPostCommand, setAppMode, refreshChart } from '../actions/commonActions'
import { RootState, AppTheme } from '../types'

import * as actions from '../actions'
import {
  MT4Connector,
  FeedEvent,
  Prices,
  BondConfigItem,
  AssetGroup,
  Error as SocketError,
  LoginSuccess,
  Tick,
  TradeObject,
  CurrentMarketState,
  CurrentPrice,
  // Signals,
  // Signal,
  // ExpiredSignals,
} from '@bdswiss/mt4-connector'
import { toast } from 'react-toastify'
import { Observable } from 'rxjs/Observable'
import { Subject } from 'rxjs/Subject'
import { Store, Action } from 'redux'

import { getCookie } from './cookie-utils'
import { setAppTheme } from './common-utils'
import { getAccountIdFromUrlPath, getUrlParam } from './url-utils'
import { fulfillBonds } from '../actions/bondActions'
import { I18n } from 'react-redux-i18n'

import TradeToast from '../components/TradeToast'
// import { fulfillExpiredSignals, fulfillSignal, fulfillSignals } from '../actions/signalsActions'

// initialize the application
export function init(config: any, store: Store<RootState>) {
  const initialLogin = getUrlParam('login') || getAccountIdFromUrlPath()
  const locale = getUrlParam('locale')
  const embedded = getUrlParam('embedded')
  const symbol = getCookie('currentSymbol')
  const dismissedRiscDisclaimer = getCookie('dismissedRiscDisclaimer') === 'true'
  const devMode = getCookie('showMustGoOn')
  const forceBonds = /^\/bonds.*/i.test(window.location.pathname)
  // const demoLoginType = demoLogin ? (forceBonds ? 'bonds' : 'forex') : undefined
  // const parentUrl = document.referrer;
  setAppTheme((getCookie('theme') || 'Light') as AppTheme)

  let graphQLUrl = config.defaultWebApiUrl

  if (embedded) {
    window.addEventListener(
      'message',
      (e: any) => {
        if (!config.allowedPostMessageOrigins.includes(e.origin) && !devMode) {
          return
        }
        if (e.source !== window && e.data && e.data.length > 0) {
          const assets: string[] =
            (e.data as string).search(',') > -1 ? e.data.split(',') : [e.data]
          // const assets: string[] =  e.data.split(',');
          assets
            .map(a => a.replace('*', '#').split('|'))
            .forEach(assetTokens => {
              const [asset, timescale, buysell, takeProfit, stopLoss] = assetTokens
              const tradeType = buysell && /sell/i.test(buysell) ? 'sell' : 'buy'
              // -> Select the Appropriate asset chart and the proper here <-
              store.dispatch(
                triggerPostCommand(
                  asset,
                  tradeType,
                  Number.parseInt(timescale, 10),
                  takeProfit ? Number.parseFloat(takeProfit) : undefined,
                  stopLoss ? Number.parseFloat(stopLoss) : undefined
                )
              )
              // -> Set take profit and stop loss <-
            })
        }
        // e.preventDefault();
      },
      false
    )
  }

  const mt4 = MT4Connector.Instance

  // Subjects are needed because onMessage returns rx6 observable that is incompatible with rx5
  const error$ = new Subject<SocketError>()
  mt4.onMessage(FeedEvent.ERROR).subscribe(error$ as any)
  const connectionError$ = new Subject<any>()
  mt4.onMessage(FeedEvent.DISCONNECTED).subscribe(connectionError$ as any)
  const reconnected$ = new Subject<any>()
  mt4.onMessage(FeedEvent.CONNECTED).subscribe(reconnected$ as any)

  const loginSuccess$ = new Subject<LoginSuccess>()
  mt4.onMessage(FeedEvent.LOGIN_SUCCESS).subscribe(loginSuccess$ as any)
  const prices$ = new Subject<Prices>()
  mt4.onMessage(FeedEvent.PRICES).subscribe(prices$ as any)
  const loginInit$ = Observable.zip(
    loginSuccess$,
    prices$,
    (login: LoginSuccess, prices: Prices) => {
      return Observable.of<Action>(fulfillPrices(prices), fulfillLogin(login))
    }
  ).mergeAll()

  const assetGroups$ = new Subject<AssetGroup[]>()
  mt4.onMessage(FeedEvent.ASSET_GROUPS).subscribe(assetGroups$ as any)
  const orderOpened$ = new Subject<TradeObject>()
  mt4.onMessage(FeedEvent.ORDER_OPENED).subscribe(orderOpened$ as any)
  const orderClosed$ = new Subject<TradeObject>()
  mt4.onMessage(FeedEvent.ORDER_CLOSED).subscribe(orderClosed$ as any)
  const orderUpdated$ = new Subject<TradeObject>()
  mt4.onMessage(FeedEvent.ORDER_UPDATED).subscribe(orderUpdated$ as any)
  const pendingOrderOpened$ = new Subject<TradeObject>()
  mt4.onMessage(FeedEvent.PENDING_ORDER_OPENED).subscribe(pendingOrderOpened$ as any)
  const pendingOrderActivated$ = new Subject<TradeObject>()
  mt4.onMessage(FeedEvent.PENDING_ORDER_ACTIVATED).subscribe(pendingOrderActivated$ as any)
  const pendingOrderCancelled$ = new Subject<TradeObject>()
  mt4.onMessage(FeedEvent.PENDING_ORDER_CANCELED).subscribe(pendingOrderCancelled$ as any)
  const pendingOrderUpdated$ = new Subject<TradeObject>()
  mt4.onMessage(FeedEvent.PENDING_ORDER_UPDATED).subscribe(pendingOrderUpdated$ as any)
  const bonds$ = new Subject<BondConfigItem[]>()
  mt4.onMessage(FeedEvent.BONDS).subscribe(bonds$ as any)
  const tick$ = new Subject<Tick>()
  mt4.onMessage(FeedEvent.TICK).subscribe(tick$ as any)
  const marketStates$ = new Subject<CurrentMarketState>()
  mt4.onMessage(FeedEvent.MARKET_STATE_CHANGED).subscribe(marketStates$ as any)
  const currentPrices$ = new Subject<CurrentPrice[]>()
  mt4.onMessage(FeedEvent.CURRENT_PRICES).subscribe(currentPrices$ as any)

  // dispatch actions on incoming messages
  Observable.merge(
    loginInit$,
    orderOpened$.map((trade: TradeObject) => {
      // toast.success(I18n.t("successfulOrderPlacement", {type: trade.type, order: trade.order}));
      const { order, type } = trade
      toast(
        <TradeToast
          messageKey="successfulOrderPlacement"
          options={{ type, order }}
          onAction={() => {
            store.dispatch(setAppMode('positions'))
            store.dispatch(actions.selectPosition(order, true))
          }}
        />,
        { type: 'success' }
      )
      return fulfillOpenTrade(trade)
    }),
    orderClosed$.map((trade: TradeObject) => {
      toast.success(I18n.t('closePositionSuccessMessage', { order: trade.order }))
      return fulfillCloseTrade(trade)
    }),
    orderUpdated$.map((trade: TradeObject) => {
      // toast.success(I18n.t("positionWasUpdated", {order: trade.order}));
      const { order } = trade
      toast(
        <TradeToast
          messageKey="positionWasUpdated"
          options={{ order }}
          onAction={() => {
            store.dispatch(setAppMode('positions'))
            store.dispatch(actions.selectPosition(order, true))
          }}
        />,
        { type: 'success' }
      )
      return fulfillModifyTrade(trade)
    }),
    pendingOrderOpened$.map((trade: TradeObject) => {
      // toast.success(I18n.t("successfulPendingOrderPlacement", {type: trade.type, order: trade.order}));
      const { order, type } = trade
      toast(
        <TradeToast
          messageKey="successfulPendingOrderPlacement"
          options={{ type, order }}
          onAction={() => {
            store.dispatch(setAppMode('orders'))
            store.dispatch(actions.selectPosition(order, true))
          }}
        />,
        { type: 'success' }
      )
      return fulfillPendingTrade(trade)
    }),
    pendingOrderActivated$.map((trade: TradeObject) => {
      toast.success(I18n.t('activatedPositionSuccessMessage', { order: trade.order }))
      return fulfillActivatedTrade(trade)
    }),
    pendingOrderCancelled$.map((trade: TradeObject) => {
      toast.success(I18n.t('canceledPositionSuccessMessage', { order: trade.order }))
      return fulfillCancelTrade(trade)
    }),
    pendingOrderUpdated$.map((trade: TradeObject) => {
      // toast.success(I18n.t("pendingPositionWasUpdated", {order: trade.order}));
      const { order } = trade
      toast(
        <TradeToast
          messageKey="pendingPositionWasUpdated"
          options={{ order }}
          onAction={() => {
            store.dispatch(setAppMode('orders'))
            store.dispatch(actions.selectPosition(order, true))
          }}
        />,
        { type: 'success' }
      )
      return fulfillModifyPendingTrade(trade)
    }),
    assetGroups$.map((result: AssetGroup[]) => fulfillAssetGroups(result)),
    bonds$.map((data: BondConfigItem[]) => fulfillBonds(data)),
    reconnected$.map(() => refreshChart(true)).delay(3000),
    connectionError$.map(() => refreshChart(false)),
    tick$
      .bufferTime(300)
      .filter(buffer => buffer.length > 0)
      .map((emmitedTics: Tick[]) => {
        const ticks = new Map<string, Tick>()
        emmitedTics.forEach(tick => ticks.set(tick.s, tick))
        return ticks
      })
      .map((result: Map<string, Tick>) => fulfillTick(result)),
    marketStates$.map((result: CurrentMarketState) => fulfillCurrentMarketState(result)),
    currentPrices$.map((result: CurrentPrice[]) => fulfillCurrentPrices(result))
  ).subscribe(
    // (action: actions.TradingAction | FulfillLogin | FulfillCurrentPricesAction) => {
    action => {
      store.dispatch(action as Action)
    },
    (error: any) => toast.error(error.toString())
  )

  enum ErrorMessages {
    INVALID_CREDENTIALS = 'notAuthorized',
    INSUFFICIENT_MARGIN = 'insufficientMargin',
    NOTHING_CHANGED = 'nothingToChange',
    MARKET_CLOSE = 'marketClosed',
  }

  // connectionError$.subscribe(() => {
  //   toast.error(I18n.t('websocketConnectionFailed'), { autoClose: false })
  // })

  error$.subscribe((error: SocketError) => {
    const { type, message } = error
    let errorMessage = ''

    if (message) {
      if (message.includes('General error')) return
      if (message.includes('margin')) {
        errorMessage = ErrorMessages.INSUFFICIENT_MARGIN
      }

      if (message.includes('nothing')) {
        errorMessage = ErrorMessages.NOTHING_CHANGED
      }

      if (message.includes('market is closed')) {
        errorMessage = ErrorMessages.MARKET_CLOSE
      }

      toast.error(errorMessage! ? I18n.t(errorMessage) : message)
    } else {
      if (type === 'INVALID_CREDENTIALS') {
        errorMessage = ErrorMessages.INVALID_CREDENTIALS
      }
      console.log(JSON.stringify(error))
      toast.error(
        errorMessage ? I18n.t(errorMessage) : message ? message : I18n.t('genericErrorMessage'),
        { autoClose: false }
      )
    }
  })

  store.dispatch(
    initApp(
      config.mt4Version,
      config.mt4Platform,
      graphQLUrl,
      embedded === 'true' ? 'embedded' : 'normal',
      symbol ? symbol : config.defaultSymbol,
      forceBonds ? 'bond' : 'forex',
      dismissedRiscDisclaimer,
      locale ? locale : undefined,
      initialLogin || undefined
      // demoLoginType
    )
  )
}
